import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PERSON_FUNCTIONS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Section, F } from "@/pages/CustomerForm";
import { MailLogSection } from "@/components/MailLogSection";
import { PeopleMailDialog } from "./PeopleMailDialog";
import { ArrowLeft, ChevronLeft, ChevronRight, Save, Trash2, Mail } from "lucide-react";

const EMPTY = { name: "", email: "", phone: "", notes: "", functions: [] };
const BASE = "/einstellungen/mitwirkende";

function Functions({ value, onToggle }) {
  return (
    <div className="col-span-6">
      <Label>Funktionen</Label>
      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {PERSON_FUNCTIONS.map((f) => {
          const on = value.includes(f.value);
          return (
            <button key={f.value} type="button" onClick={() => onToggle(f.value)} data-testid={`person-fn-${f.value}`}
              className={`text-xs font-medium px-2.5 py-1.5 rounded-md border transition-colors ${on ? f.cls : "bg-white text-slate-400 border-dashed border-slate-300 hover:text-slate-600"}`}>
              {f.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Pager({ ids, id, go }) {
  const idx = ids.indexOf(id);
  if (idx < 0) return null;
  return (
    <div className="flex items-center gap-1">
      <Button variant="outline" size="sm" disabled={idx === 0} onClick={() => go(ids[idx - 1])} data-testid="person-prev"><ChevronLeft size={16} /> Vorherige</Button>
      <span className="text-sm text-slate-500 px-2" data-testid="person-pager-pos">{idx + 1} / {ids.length}</span>
      <Button variant="outline" size="sm" disabled={idx === ids.length - 1} onClick={() => go(ids[idx + 1])} data-testid="person-next">Nächste <ChevronRight size={16} /></Button>
    </div>
  );
}

export default function PersonDetail() {
  const { id } = useParams();
  const isNew = id === "neu";
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [saved, setSaved] = useState(null);
  const [ids, setIds] = useState(location.state?.ids || []);
  const [mailOpen, setMailOpen] = useState(false);
  const [logKey, setLogKey] = useState(0);

  const load = useCallback(async () => {
    if (isNew) { setForm(EMPTY); setSaved(null); return; }
    try { const { data } = await api.get(`/people/${id}`); setForm({ ...EMPTY, ...data }); setSaved(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); navigate(BASE); }
  }, [id, isNew, navigate]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!location.state?.ids && !isNew) { api.get("/people").then((r) => setIds(r.data.map((p) => p.id))).catch(() => {}); }
  }, [isNew, location.state]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (fn) => setForm((f) => ({ ...f, functions: f.functions.includes(fn) ? f.functions.filter((x) => x !== fn) : [...f.functions, fn] }));
  const dirty = saved && JSON.stringify({ ...EMPTY, ...saved }) !== JSON.stringify(form);

  const go = (nextId) => {
    if (dirty && !window.confirm("Ungespeicherte Änderungen verwerfen?")) return;
    navigate(`${BASE}/${nextId}`, { state: { ids } });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name ist erforderlich"); return; }
    const payload = { name: form.name, email: form.email, phone: form.phone, notes: form.notes, functions: form.functions };
    try {
      if (isNew) {
        const { data } = await api.post("/people", payload);
        toast.success("Angelegt"); navigate(`${BASE}/${data.id}`, { replace: true });
      } else {
        const { data } = await api.put(`/people/${id}`, payload);
        setSaved(data); setForm({ ...EMPTY, ...data }); toast.success("Gespeichert");
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async () => {
    if (!window.confirm(`${form.name} wirklich löschen?`)) return;
    try { await api.delete(`/people/${id}`); toast.success("Gelöscht"); navigate(BASE); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <Link to={BASE} className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid="person-back"><ArrowLeft size={15} /> Mitwirkende</Link>
        {!isNew && <Pager ids={ids} id={id} go={go} />}
      </div>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <h1 className="font-display text-3xl font-bold text-slate-900" data-testid="person-detail-title">{isNew ? "Person hinzufügen" : saved?.name}</h1>
        <div className="flex gap-2">
          {!isNew && <Button variant="outline" disabled={!saved?.email || !user?.is_chef} title={user?.is_chef ? "E-Mail senden" : "Mailversand nur für den Chefredakteur"} onClick={() => setMailOpen(true)} data-testid="person-detail-mail"><Mail size={15} className="mr-1.5" /> E-Mail</Button>}
          {!isNew && <Button variant="outline" onClick={remove} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="person-delete"><Trash2 size={15} className="mr-1.5" /> Löschen</Button>}
          <Button onClick={save} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="person-save-button"><Save size={15} className="mr-1.5" /> Speichern{dirty ? " *" : ""}</Button>
        </div>
      </div>
      <div className="space-y-5">
        <Section title="Kontakt" testid="section-person">
          <F label="Name *" span={6} value={form.name} onChange={(v) => set("name", v)} testid="person-name-input" />
          <F label="E-Mail" span={3} type="email" value={form.email} onChange={(v) => set("email", v)} testid="person-email-input" />
          <F label="Telefon" span={3} value={form.phone} onChange={(v) => set("phone", v)} testid="person-phone-input" />
          <Functions value={form.functions} onToggle={toggle} />
          <div className="col-span-6"><Label>Notiz</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className="mt-1" data-testid="person-notes-input" /></div>
        </Section>
        {!isNew && <MailLogSection kind="people" customerId={id} customerName={saved?.name} reloadKey={logKey} />}
      </div>
      <PeopleMailDialog open={mailOpen} onOpenChange={(o) => { setMailOpen(o); if (!o) { setLogKey((k) => k + 1); } }} recipients={saved ? [saved] : []} />
    </div>
  );
}
